/**
 * The engagement gate's pure half: when a gate stands, and the chips a round
 * of it deals. The React half is `@bindery/ui/components/reader/gate-challenge`;
 * the confounders come from `./confounders`. Replaces the `buildChallenge`
 * that retells (`reader/gloss-popover.tsx`) and lekterion
 * (`read/EngagementGate.tsx`) each carried verbatim, and the two apps' own
 * "is the gate standing" derivations.
 *
 * A gate withholds something already cached (a free translation) behind a
 * quick orthographic match: the reader picks the word they selected out of
 * near-misses. Nothing uncached is gated — glossing it costs a model call, so
 * there is nothing free to withhold — and a word with no usable confounder
 * falls open ungated.
 */

import { shuffleArray } from "../std/shuffle";
import { applySurfacePattern, type NormalizeWord } from "./confounders";

/** One chip of a round. */
export interface Chip {
  /**
   * Stable per-position id. It is the React key, so a reshuffle reuses each
   * button's DOM node and keyboard focus survives a wrong pick.
   */
  id: number;
  /** The surface the reader sees (the word's own case and quote glyphs). */
  form: string;
  correct: boolean;
}

/** A reader's pick, as the gate reports it to its shell. */
export interface GatePick {
  /** The surface form on the chip. */
  form: string;
  /** `form` normalized — the value a log or `recordMatch` compares to the word. */
  chosen: string;
  correct: boolean;
}

/** How many confounders a round deals beside the word. */
const PICKS_PER_ROUND = 2;
/** The top-ranked pool those picks are drawn from. */
const POOL_SIZE = 6;

/**
 * The confounders a round may deal, as the surfaces their chips will show —
 * with every one that would look like the word, or like another chip,
 * removed. Re-surfacing can collide: a Greek confounder that differs only in
 * a final sigma re-cases to the correct word (ς→Σ), and two confounders can
 * re-case to one form. A collision dealt as a chip is two identical buttons,
 * one right and one wrong, and a pick on the "wrong" twin normalizes to the
 * word itself — lekterion's `recordMatch` derives `correct` from exactly that
 * comparison, so it logged a pick the UI treated as wrong as CORRECT (code
 * review 2026-09-30, 08 #5). Comparing both the surface and the normalized
 * form catches the collisions either side can see. Ranking order is kept.
 */
export function challengeableConfounders(
  word: string,
  confounders: readonly string[],
  normalize: NormalizeWord,
): string[] {
  const seenForms = new Set([word]);
  const seenKeys = new Set([normalize(word)]);
  const out: string[] = [];
  for (const c of confounders) {
    const form = applySurfacePattern(word, c);
    const key = normalize(form);
    if (seenForms.has(form) || seenKeys.has(key)) continue;
    seenForms.add(form);
    seenKeys.add(key);
    out.push(form);
  }
  return out;
}

/**
 * One round: the word plus up to two confounders drawn at random from the
 * top-ranked pool, shuffled. `forms` is the output of
 * {@link challengeableConfounders} — already re-surfaced and collision-free,
 * so every chip is distinct. Ids are assigned by final position, and every
 * round of one gate deals the same number of chips, which is what lets a
 * reshuffle update buttons in place.
 */
export function buildChallenge(word: string, forms: readonly string[]): Chip[] {
  const picks = shuffleArray(forms.slice(0, POOL_SIZE)).slice(
    0,
    PICKS_PER_ROUND,
  );
  const chips: Omit<Chip, "id">[] = [
    { form: word, correct: true },
    ...picks.map((form) => ({ form, correct: false })),
  ];
  return shuffleArray(chips).map((chip, id) => ({ ...chip, id }));
}

/**
 * Where the gate stands for one selection.
 *
 * - `pending` — not yet known: the cache lookups or the confounders are
 *   still loading. A shell must treat this as LOCKED: withhold the cached
 *   result and every action that would reveal it. Lekterion's selection menu
 *   left its Gloss / Translate / Read-aloud items enabled through this
 *   window, so a fast click opened the very translation the gate exists to
 *   withhold (code review 2026-09-30, 08 #6).
 * - `gated` — a challenge stands.
 * - `open` — nothing to withhold (not a single word, nothing cached, no
 *   usable confounder) or the reader has matched it.
 */
export type GateStatus = "pending" | "gated" | "open";

export function gateStatus({
  singleWord,
  cached,
  forms,
  unlocked,
}: {
  /** Only a single word is gated; phrases are an ungated reveal. */
  singleWord: boolean;
  /** Is anything cached for it? `undefined` while the lookups resolve. */
  cached: boolean | undefined;
  /** {@link challengeableConfounders}; `null` while they load. */
  forms: readonly string[] | null;
  /** The reader has matched it. Never persisted — reselecting re-gates. */
  unlocked: boolean;
}): GateStatus {
  if (!singleWord || unlocked) return "open";
  if (cached === undefined) return "pending";
  if (!cached) return "open";
  if (forms === null) return "pending";
  return forms.length > 0 ? "gated" : "open";
}
